import type { WayPoint } from '$lib/components/search/itinerary/types';
import { page } from '$app/stores';
import { addNotification } from '$lib/components/notifications/notifications';
import { derived, get, readonly, writable } from 'svelte/store';
import { getMyLocation } from './userLocation';

export const MAX_STEPS = 17;

export type ItineraryStore = ReturnType<typeof createItineraryStore>;

export function createItineraryStore(itineraryFromSearchParams?: WayPoint[]) {
	let _idCounter = 0;

	const initial: WayPoint[] = getItinialItinerary(itineraryFromSearchParams);

	const itinerary = writable(initial);

	function isArrival(wp: WayPoint) {
		return wp.coordinates !== undefined && wp.isArrival === true;
	}

	const itineraryHasArrival = derived(
		itinerary,
		$itinerary => $itinerary.some(isArrival),
	);

	const itineraryHasMyLocation = derived(itinerary, $itinerary =>
		$itinerary.some(wp => wp.isMyLocation));

	function getItinialItinerary(itineraryFromSearchParams?: WayPoint[]): WayPoint[] {
		const pageStore = get(page);

		const defaultDeparture = {
			index: 0,
			isArrival: false,
			id: _idCounter++,
		};

		const defaultArrival = {
			index: 1,
			isArrival: true,
			id: _idCounter++,
		};

		const waypoints = pageStore.data?.waypointsInfo?.waypoints;

		if (waypoints) {
			_idCounter = waypoints.length;
			return waypoints;
		}

		if (itineraryFromSearchParams?.length) {
			_idCounter = itineraryFromSearchParams.length;
			return itineraryFromSearchParams;
		}

		return [defaultDeparture, defaultArrival];
	}

	function updateWaypoint(wp: WayPoint) {
		itinerary.update(($itinerary) => {
			$itinerary[wp.index] = {
				...$itinerary[wp.index],
				label: wp.label,
				coordinates: wp.coordinates,
				isMyLocation: wp.isMyLocation,
				addressType: wp.addressType,
			};
			return $itinerary;
		});
	}

	function initialWaypoints(waypoints: WayPoint[]) {
		itinerary.set(waypoints);
		_idCounter = waypoints.length;
	}

	function setArrival(wp: Omit<WayPoint, 'index' | 'id'>) {
		itinerary.update(($itinerary) => {
			const index = $itinerary.length - 1;
			$itinerary[index] = { ...wp, index, isArrival: true, id: _idCounter++ };
			return $itinerary;
		});
	}

	function addWayPoint(wp?: Omit<WayPoint, 'index' | 'id'>) {
		itinerary.update(($itinerary) => {
			if ($itinerary.length >= MAX_STEPS) {
				addNotification('itinerary.max-steps-reached');
				return $itinerary;
			}
			const index = $itinerary.length - 1;
			$itinerary.splice(index, 0, { ...wp, index, id: _idCounter++ });
			return setIndexes($itinerary);
		});
	}

	function addToItinerary(wp: Omit<WayPoint, 'index' | 'id'>) {
		if (!get(itineraryHasArrival)) {
			setArrival(wp);
		} else {
			addWayPoint(wp);
		}
	}

	function removeWayPoint(index: number) {
		itinerary.update(($itinerary) => {
			if ($itinerary.length < 3) {
				return $itinerary; // make sure there are always 2 at least
			}
			$itinerary.splice(index, 1);
			return setIndexes($itinerary);
		});
	}

	function updateIndex(oldIndex: number, newIndex: number) {
		itinerary.update(($itinerary) => {
			const curr = $itinerary[oldIndex];
			$itinerary.splice(oldIndex, 1);
			$itinerary.splice(newIndex, 0, curr);
			return setIndexes($itinerary);
		});
	}

	function reverse() {
		itinerary.update($itinarary => setIndexes($itinarary.reverse()));
	}

	function setIndexes(waypoints: WayPoint[]) {
		const last = waypoints.length - 1;
		for (let i = 0; i < waypoints.length; i++) {
			waypoints[i].index = i;
			waypoints[i].isArrival = i === last;
		}

		return waypoints;
	}

	function reloadId(wp: WayPoint) {
		itinerary.update(($itinerary) => {
			$itinerary[wp.index] = {
				...$itinerary[wp.index],
				id: _idCounter++,
			};
			return $itinerary;
		});
	}

	return {
		itinerary: readonly(itinerary),
		itineraryHasArrival,
		itineraryHasMyLocation,
		addWayPoint,
		removeWayPoint,
		updateWaypoint,
		initialWaypoints,
		reloadId,
		updateIndex,
		reverse,
		setArrival,
		addToItinerary,
	};
}

export async function getWaypointCoordinates(wp: WayPoint) {
	if (wp.coordinates === 'MY_LOC') {
		const position = await getMyLocation();
		return {
			lng: position.coords.longitude,
			lat: position.coords.latitude,
		};
	}

	return wp.coordinates;
}
